
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ReferralHandler } from '@/components/affiliate/ReferralHandler';
import { validateReferralCode, storeReferralCode } from '@/utils/AffiliateUtils';
import { ArrowRight, Clock, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface ReferralError {
  code: string;
  message: string;
  suggestion: string;
}

export default function Refer() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isValidCode, setIsValidCode] = useState(false);
  const [affiliateName, setAffiliateName] = useState('');
  const [error, setError] = useState<ReferralError | null>(null);
  const [attemptedCode, setAttemptedCode] = useState<string | null>(null);

  useEffect(() => {
    const validateCode = async () => {
      if (!code) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setAttemptedCode(code);
        
        // First validate the code format
        if (!/^[a-zA-Z0-9]{4,20}$/.test(code)) {
          setError({
            code: 'INVALID_FORMAT',
            message: 'The referral code format is invalid',
            suggestion: 'Referral codes should be 4-20 characters with only letters and numbers'
          });
          setIsValidCode(false);
          setLoading(false);
          return;
        }
        
        console.log('Validating referral code:', code);
        
        // Then check if it exists in our system
        const isValid = await validateReferralCode(code);
        setIsValidCode(isValid);
        
        if (isValid) {
          console.log('Referral code validated successfully:', code);
          
          // Store valid code in localStorage
          storeReferralCode(code);
          
          // Try to get affiliate name if available
          try {
            const { data } = await fetch(
              `/api/get-affiliate-name?code=${encodeURIComponent(code.toLowerCase())}`
            ).then(res => res.json());
            
            if (data?.name) {
              setAffiliateName(data.name);
            }
          } catch (nameError) {
            console.warn('Could not fetch affiliate name:', nameError);
            // Non-critical error, we continue without the name
          }
        } else {
          console.error('Invalid referral code:', code);
          setError({
            code: 'CODE_NOT_FOUND',
            message: 'This referral code doesn\'t exist or has expired',
            suggestion: 'Please check the code and try again, or continue without a referral'
          });
        }
      } catch (error) {
        console.error('Error validating referral code:', error);
        setIsValidCode(false);
        setError({
          code: 'SERVER_ERROR',
          message: 'We encountered a problem validating this referral code',
          suggestion: 'Please try again later or continue without a referral'
        });
      } finally {
        setLoading(false);
      }
    };

    validateCode();
  }, [code, navigate]);

  const handleTryAgain = async () => {
    setLoading(true);
    try {
      // Re-trigger the validation logic with the attempted code
      const codeToTry = attemptedCode || code;
      if (!codeToTry) {
        toast.error("No referral code to validate");
        setLoading(false);
        return;
      }
      
      console.log('Retrying validation for code:', codeToTry);
      const isValid = await validateReferralCode(codeToTry);
      setIsValidCode(isValid);
      
      if (isValid) {
        setError(null);
        storeReferralCode(codeToTry);
        toast.success("Referral code is valid!");
        
        // Try to get affiliate name again
        try {
          const { data } = await fetch(
            `/api/get-affiliate-name?code=${encodeURIComponent(codeToTry.toLowerCase())}`
          ).then(res => res.json());
          
          if (data?.name) {
            setAffiliateName(data.name);
          }
        } catch (nameError) {
          console.warn('Could not fetch affiliate name:', nameError);
        }
      } else {
        toast.error("The referral code is still invalid.");
        setError({
          code: 'CODE_NOT_FOUND',
          message: 'This referral code doesn\'t exist or has expired',
          suggestion: 'Please check the code and try again, or continue without a referral'
        });
      }
    } catch (err) {
      console.error("Error retrying code validation:", err);
      toast.error("Failed to validate the code. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualCodeEntry = () => {
    navigate('/auth', { state: { showSignup: true } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white p-4">
      {/* Include the referral handler to process the code */}
      <ReferralHandler />
      
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>
              {loading ? 'Checking Referral...' : 
               isValidCode ? 'Special Invitation' : 'Referral Issue'}
            </CardTitle>
            <CardDescription>
              {loading ? 'Please wait while we verify this referral link' :
               isValidCode ? `You've been referred${affiliateName ? ' by ' + affiliateName : ''}!` : 
                             error?.message || 'This referral link is invalid or expired'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-6">
                <Clock className="h-12 w-12 text-blue-500 animate-pulse" />
                <p className="mt-4 text-center text-muted-foreground">
                  Verifying referral link...
                </p>
                <div className="w-full mt-4 space-y-2">
                  <Skeleton className="h-4 w-3/4 mx-auto" />
                  <Skeleton className="h-4 w-1/2 mx-auto" />
                </div>
              </div>
            ) : isValidCode ? (
              <>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-center">
                    Welcome to Edaptica! You've been invited through a special referral link.
                  </p>
                  <p className="text-center text-sm text-blue-600 mt-2">
                    Create your account now to enjoy all the benefits!
                  </p>
                </div>
                
                <div className="space-y-4">
                  <Button asChild className="w-full">
                    <Link to="/auth">
                      Sign Up Now <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/">
                      Continue to Homepage
                    </Link>
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center justify-center py-6">
                  {error?.code === 'SERVER_ERROR' ? (
                    <AlertCircle className="h-12 w-12 text-amber-500" />
                  ) : (
                    <X className="h-12 w-12 text-red-500" />
                  )}
                  <p className="mt-4 text-center text-muted-foreground">
                    {error?.suggestion || "This referral link is invalid or has expired."}
                  </p>
                  {attemptedCode && (
                    <p className="mt-2 text-sm text-center text-gray-500">
                      Attempted code: <span className="font-mono bg-gray-100 px-1 rounded">{attemptedCode}</span>
                    </p>
                  )}
                </div>
                
                <div className="space-y-3 mt-4">
                  <Button onClick={handleTryAgain} variant="outline" className="w-full">
                    Try Again
                  </Button>
                  
                  <Button onClick={handleManualCodeEntry} className="w-full">
                    Enter Code Manually
                  </Button>
                  
                  <Button asChild variant="ghost" className="w-full">
                    <Link to="/">
                      Go to Homepage
                    </Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-center pt-2 pb-4 border-t">
            <p className="text-xs text-center text-muted-foreground">
              Referral codes can be entered during signup even if you don't have a referral link.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
